import { useCallback, useEffect, useState } from 'react';

import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';

import { toaster } from 'components/toaster/toaster';
import { useIsMobileOrderForDriveThru } from 'hooks/use-is-mobile-order-for-drive-thru';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logButtonClick,
  logEvent,
} from 'state/amplitude';
import { IVehicleDescriptionChange } from 'state/cart/types';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { hapticSelection } from 'utils/haptic';

import { useCheckoutContext } from '../../hooks/use-checkout-context';
import { useCheckoutPriceOrder } from '../../hooks/use-checkout-price-order';

import { UseServiceModeWidget } from './types';

const useVehicleDescription = () => {
  const {
    setCheckoutState,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
    isVehicleDescriptionEnabled,
  } = useCheckoutContext();

  const setVehicleDescription = useCallback(
    (vehicleDescription: IVehicleDescriptionChange) => {
      setCheckoutState(prev => ({
        ...prev,
        vehicleDescription: {
          value: vehicleDescription.value,
          hasError: vehicleDescription.hasError ?? prev.vehicleDescription.hasError,
        },
      }));
    },
    [setCheckoutState]
  );

  const isSignUpAfterCart = useIsSignUpAfterCart();

  return {
    setVehicleDescription,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
    isSignUpAfterCart,
    displayVehicleDescription: isVehicleDescriptionEnabled,
  };
};

export const useServiceModeWidget = (): UseServiceModeWidget => {
  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);

  const { isStoreAvailableForPickup } = useStoreAvailabilityFns();
  const { store } = useStoreContext();
  const enableMinimalCartUi = useMinimalCartUi();

  const {
    selectServiceMode,
    serviceMode: activeServiceMode,
    isDelivery,
    getServiceModeOptions,
  } = useServiceModeContext();

  const [hasInvalidDeliveryOptions, setHasInvalidDeliveryOptions] = useState(false);
  // Note that this happens asynchronously, so the initial value of `isPickupTimeAvailable` will be `undefined`.
  const isMobileOrderForDriveThru = useIsMobileOrderForDriveThru(activeServiceMode, store.number);
  const {
    setVehicleDescription,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
    displayVehicleDescription,
  } = useVehicleDescription();

  const { priceCheckoutOrderForServiceMode } = useCheckoutPriceOrder();

  const isDisabled = !isDelivery && !isStoreAvailableForPickup(store);
  const [isServiceModeSelectorOpen, setIsServiceModeSelectorOpen] = useState(false);

  // Present the Pickup Time Selector if:
  // - Order injection is TIMED_FIRE
  const isOrderTimedFire = orderInjectFlow === OrderInjectionFlowVariations.TIMED_FIRE;

  const handleOpenServiceModeSelector = () => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        name: CustomEventNames.BUTTON_CLICK_SERVICE_MODE_WIDGET,
      },
    });

    setIsServiceModeSelectorOpen(true);
  };

  const handleSelectServiceMode = useCallback(
    (mode: ServiceMode) => {
      hapticSelection();
      selectServiceMode(mode);

      // When Minimal Cart UI is enabled, switching service mode on Checkout must reprice
      // so totals/serverOrder stay in sync with the newly selected mode.
      if (enableMinimalCartUi) {
        priceCheckoutOrderForServiceMode(mode);
      }

      logEvent(CustomEventNames.BUTTON_CLICK_PICKUP, EventTypes.Other);
      setIsServiceModeSelectorOpen(false);
    },
    [enableMinimalCartUi, priceCheckoutOrderForServiceMode, selectServiceMode]
  );

  const serviceModeOptions = getServiceModeOptions(store).filter(
    serviceMode => serviceMode.isAvailable
  );
  const selectedServiceMode = serviceModeOptions.find(serviceMode => serviceMode.isSelected);
  const selectedServiceModeAccessibilityLabel = `${t('currentServiceMode')}: ${
    selectedServiceMode?.heading
  }`;

  // If actual service mode is not available, then try to select the next available service mode
  useEffect(() => {
    if (isDelivery) {
      return;
    }

    if (!selectedServiceMode?.isAvailable) {
      const unavailableServiceModeHeading = selectedServiceMode?.heading;
      const nextAvailableServiceMode = getServiceModeOptions(store).find(
        serviceModeOption => serviceModeOption.isAvailable
      );

      if (nextAvailableServiceMode) {
        selectServiceMode(nextAvailableServiceMode.name);
        toaster.informative(
          t('serviceModeSwitchedAutomatically', {
            modeName: unavailableServiceModeHeading,
            nextModeName: nextAvailableServiceMode.heading,
            defaultValue: `${unavailableServiceModeHeading} is unavailable. Switched automatically to ${nextAvailableServiceMode.heading}. You can change it if needed.`,
          })
        );
      }
    }
  }, [selectedServiceMode, getServiceModeOptions, store, selectServiceMode, isDelivery]);

  return {
    selectedServiceMode,
    selectedServiceModeAccessibilityLabel,
    serviceModeOptions,
    handleOpenServiceModeSelector,
    handleSelectServiceMode,
    isServiceModeSelectorOpen,
    setIsServiceModeSelectorOpen,
    isMobileOrderForDriveThru,
    setVehicleDescription,
    vehicleDescriptionValue,
    vehicleDescriptionHasError,
    displayVehicleDescription,
    isOrderTimedFire,
    hasInvalidDeliveryOptions,
    setHasInvalidDeliveryOptions,
    isDisabled,
  };
};
